import React, { useContext } from 'react';

import styled from '@tanium/react-emotion-9';
import { css } from '@tanium/style-system';

import { UpdateFilterArgs } from '../hooks/useAdvancedFilters';
import { FilterLabelText } from '../themeDefinitions';
import AdvancedFiltersContext from './AdvancedFiltersContext';

export interface RenderPropArgs {
  /**
   * Updates the state of the AdvancedFilters to include the value for this AdvancedFilterInput.
   */
  updateFilter: (args: Pick<UpdateFilterArgs, 'value' | 'displayValue'>) => void;
  /**
   * The value of the AdvancedFilterInput.
   */
  filterValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * Id for accessibility. Setting this id on an input will allow it to be focused upon clicking the
   * AdvancedFilterInput label.
   */
  idForInput: string;
}

export interface AdvancedFilterInputProps {
  /**
   * The label to display above the AdvancedFilterInput.
   */
  label?: string;
  /**
   * Render prop for the field displayed within a AdvancedFilterInput.
   */
  children: (args: RenderPropArgs) => React.ReactNode;
  /**
   * The dataKey of the filter being changed. This key is the same key used to persist the value in
   * the AdvancedFilters's value.
   */
  dataKey: string;
}

const Label = styled('label')(
  css({
    color: FilterLabelText,
    fontSize: 12,
    lineHeight: 1,
    mb: 1,
  }),
);

const InputWrapper = styled('div')(
  css({
    mb: 1,
    mr: 2,
    display: 'inline-block',
  }),
);

/**
 * Utility component for creating AdvancedFilterInputs to be used
 * within the AdvancedFilters component.
 */
const AdvancedFilterInput = ({ label, children, dataKey }: AdvancedFilterInputProps) => {
  const { prefix, value, updateFilterValue } = useContext(AdvancedFiltersContext);
  const idForInput = `${prefix}-${dataKey}`;
  return (
    <InputWrapper>
      {label && <Label htmlFor={idForInput}>{label}</Label>}
      <div>
        {children?.({
          idForInput,
          filterValue: value?.[dataKey]?.value,
          updateFilter: ({ value: newFilterValue, displayValue }) => {
            updateFilterValue({
              dataKey,
              label,
              value: newFilterValue,
              displayValue: displayValue || newFilterValue,
            });
          },
        })}
      </div>
    </InputWrapper>
  );
};

export default AdvancedFilterInput;
